/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import Api2LastValueData from '@/components/api/Api2LastValueData';
import TimeSeriesValueCard from '@/components/kpi/TimeSeriesValueCard';
import TimeSeriesValueCarousel from '@/components/kpi/TimeSeriesValueCarousel';
import type { Api2ValueDataItem, Api2ValueDataValue } from '@/lib/overview/api2ValueData';
import type { TimeSeriesTrafficLightConfig } from '@/lib/timeSeries/timeSeriesValue';

interface KpiCarouselApi2LastValueModalConfig {
    buttonLabel?: string;
    message: string;
    title?: string;
}

export interface KpiCarouselApi2LastValueItem {
    conversion?: number;
    fractionDigits?: number;
    key: string;
    offset?: number;
    series?: string;
    title?: string;
    trafficLight?: TimeSeriesTrafficLightConfig;
    unit?: string | null;
    urn: string;
}

interface KpiCarouselApi2LastValueProps {
    autoAdvanceMs?: number;
    date: string;
    debug?: boolean;
    info?: string;
    items: KpiCarouselApi2LastValueItem[];
    link?: string;
    modal?: KpiCarouselApi2LastValueModalConfig;
    showDate?: boolean;
    showLegend?: boolean;
}

function getItemValueBySeries<T>(
    item: Api2ValueDataItem | undefined,
    value: Api2ValueDataValue<T> | undefined,
    series: string,
): T | null {
    if (value === undefined) {
        return null;
    }

    if (!Array.isArray(value)) {
        return value;
    }

    const index = item?.urn.entries().findIndex((entry) => (
        entry.key === series || entry.aliases?.includes(series)
    )) ?? -1;

    return index >= 0 ? value[index] ?? null : null;
}

function KpiCarouselApi2LastValue({
    autoAdvanceMs,
    date,
    debug = false,
    info,
    items,
    link,
    modal,
    showDate = false,
    showLegend = false,
}: KpiCarouselApi2LastValueProps) {
    return (
        <Api2LastValueData
            conversion={items.map((item) => item.conversion ?? 1)}
            date={date}
            fractionDigits={items.map((item) => item.fractionDigits ?? 2)}
            offset={items.map((item) => item.offset ?? 0)}
            title={items.map((item) => item.title)}
            unit={items.map((item) => item.unit)}
            urn={[items.map((item) => item.urn)]}
        >
            {({ items: resolvedItems, isLoading, loadingLabel }) => (
                <TimeSeriesValueCarousel
                    autoAdvanceMs={autoAdvanceMs}
                    isLoading={isLoading}
                    items={items.map((item) => {
                        const selectedSeries = item.series ?? item.urn;
                        const resolvedItem = resolvedItems[0];
                        const value = resolvedItem?.rawValue.get(selectedSeries) ?? null;

                        return {
                            key: item.key,
                            content: (
                                <TimeSeriesValueCard
                                    conversion={debug ? 1 : (item.conversion ?? 1)}
                                    displayValue={debug && value !== null ? String(value) : null}
                                    errorMessage={getItemValueBySeries(resolvedItem, resolvedItem?.errorMessage, selectedSeries) ?? ''}
                                    fractionDigits={item.fractionDigits ?? 2}
                                    info={debug ? undefined : info}
                                    link={link}
                                    metaDate={debug ? null : (showDate ? getItemValueBySeries(resolvedItem, resolvedItem?.date, selectedSeries) : null)}
                                    modal={modal}
                                    offset={debug ? 0 : (item.offset ?? 0)}
                                    showLegend={debug ? false : showLegend}
                                    title={item.title}
                                    trafficLight={debug ? undefined : item.trafficLight}
                                    unit={debug ? null : item.unit}
                                    value={value}
                                />
                            ),
                        };
                    })}
                    loadingLabel={loadingLabel}
                />
            )}
        </Api2LastValueData>
    );
}

export default KpiCarouselApi2LastValue;
